"use client";

import Link from "next/link";
import { useMemo } from "react";
import { splitTitle } from "@/lib/utils/title";
import { shuffleWithSeed } from "@/lib/utils/shuffle";

export type SlimTool = {
  slug: string;
  name: string;
  description: string;
  href: string;
  category: string;
};

export type SlimCategory = {
  slug: string;
  name: string;
  description: string;
  href: string;
};

type Props = {
  currentSlug: string;
  allTools: SlimTool[];
  allCategories: SlimCategory[];
};

function getStableSeed(value: string) {
  return Array.from(value).reduce(
    (seed, character) => seed + character.charCodeAt(0),
    0,
  );
}

export default function DiscoverySections({
  currentSlug,
  allTools,
  allCategories,
}: Props) {
  const seed = getStableSeed(currentSlug);

  const { otherTools, randomTools, categories } = useMemo(() => {
    const currentTool = allTools.find((tool) => tool.slug === currentSlug);

    // 1. Other Tools
    const sameCategoryTools = currentTool
      ? allTools.filter(
          (tool) =>
            tool.category === currentTool.category && tool.slug !== currentSlug,
        )
      : [];
    const shuffledOther = shuffleWithSeed(sameCategoryTools, seed).slice(0, 10);

    // 2. Random Tools
    const shuffledRandom = shuffleWithSeed(allTools, seed).slice(0, 10);

    // 3. Categories
    const shuffledCategories = shuffleWithSeed(allCategories, seed).slice(0, 8);

    return {
      otherTools: shuffledOther,
      randomTools: shuffledRandom,
      categories: shuffledCategories,
    };
  }, [currentSlug, allTools, allCategories, seed]);

  return (
    <>
      {/* --- Other Tools Section --- */}
      {otherTools.length > 0 && (
        <section className="space-y-10">
          <div className="h-1 w-20 bg-[#fb397d] mx-auto rounded" />
          <h2 className="text-2xl font-bold text-center text-[#5b32b4]">
            Other Related Free Tools
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {otherTools.map((tool) => (
              <Link
                key={tool.slug}
                href={tool.href}
                prefetch={false}
                className="rounded-3xl border p-6 bg-white shadow-sm hover:shadow-xl transition hover:-translate-y-1 space-y-3 group"
              >
                <h3 className="text-lg font-semibold text-[#2b1d3a]">
                  {splitTitle(tool.name)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {tool.description}
                </p>
                <span className="text-sm font-semibold text-[#c3003a] group-hover:underline">
                  Try now →
                </span>
              </Link>
            ))}
          </div>
          <div className="flex justify-center pt-4">
            <Link
              href="/tools"
              prefetch={false}
              className="px-6 py-3 rounded-full bg-[#5b32b4] text-white font-semibold shadow hover:opacity-90 transition"
            >
              View All Tools →
            </Link>
          </div>
        </section>
      )}

      {/* --- Random Tools Section --- */}
      <section className="py-12 px-4 max-w-7xl mx-auto space-y-12">
        <div className="flex flex-col items-center space-y-3">
          <div className="flex items-center space-x-2">
            <span className="h-px w-8 bg-[#fb397d]"></span>
            <span className="text-[#c3003a] font-bold text-xs uppercase tracking-[0.2em]">
              Quick Access
            </span>
            <span className="h-px w-8 bg-[#fb397d]"></span>
          </div>
          <h2 className="text-3xl font-extrabold text-center text-[#5b32b4] sm:text-4xl">
            Explore More Free Online Tools
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {randomTools.map((tool) => (
            <Link
              key={tool.slug}
              href={tool.href}
              prefetch={false}
              className="group relative overflow-hidden rounded-[2rem] border border-gray-100 p-7 bg-white hover:bg-gradient-to-br hover:from-white hover:to-[#5b32b4]/5 shadow-[0_2px_15px_-3px_rgba(0,0,0,0.07)] hover:shadow-[0_20px_25px_-5px_rgba(91,50,180,0.1)] transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between"
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 pointer-events-none"
                style={{
                  backgroundImage:
                    "radial-gradient(#5b32b4 0.5px, transparent 0.5px)",
                  backgroundSize: "10px 10px",
                }}
              />
              <div className="relative z-10 space-y-4">
                <div className="flex items-start justify-between">
                  <h3 className="text-xl font-bold text-[#2b1d3a] leading-tight group-hover:text-[#5b32b4] transition-colors">
                    {splitTitle(tool.name)}
                  </h3>
                  <div className="text-[#5b32b4]/20 group-hover:text-[#c3003a] transition-colors">
                    <svg
                      className="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                    </svg>
                  </div>
                </div>
                <p className="text-sm text-gray-500 leading-relaxed font-medium">
                  {tool.description}
                </p>
              </div>
              <div className="relative z-10 pt-6">
                <span className="inline-flex items-center justify-center w-full py-2.5 rounded-xl bg-gray-50 text-[#c3003a] text-sm font-bold group-hover:bg-[#fb397d] group-hover:text-white transition-all duration-300 shadow-sm">
                  Use Tool
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="flex justify-center pt-4">
          <Link
            href="/tools"
            prefetch={false}
            className="group flex items-center space-x-3 px-8 py-4 rounded-2xl bg-[#2b1d3a] text-white font-bold shadow-lg hover:bg-[#5b32b4] transition-all duration-300 transform hover:scale-105"
          >
            <span>Explore Full Library</span>
            <svg
              className="w-5 h-5 group-hover:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </Link>
        </div>
      </section>

      {/* --- Categories Section --- */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-16">
        <div className="relative text-center space-y-4">
          <span className="inline-block px-4 py-1.5 mb-2 text-xs font-bold tracking-widest uppercase text-[#c3003a] bg-[#fb397d]/10 rounded-full">
            Utility Suite
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#5b32b4]">
            Browse Tool Categories
          </h2>
          <p className="text-gray-500 max-w-3xl mx-auto text-base md:text-lg font-medium leading-relaxed">
            Explore our powerful collection of online tools designed to help you
            convert, edit, optimize, and analyze your images and documents
            instantly — all directly in your browser.
          </p>
          <div className="h-1.5 w-24 bg-gradient-to-r from-[#5b32b4] to-[#fb397d] mx-auto rounded-full mt-6" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category) => (
            <Link
              target="_blank"
              key={category.slug}
              href={category.href}
              prefetch={false}
              className="group relative flex flex-col p-8 bg-white border border-gray-100 rounded-3xl shadow-[0_4px_20px_-4px_rgba(91,50,180,0.1)] hover:shadow-[0_20px_40px_-12px_rgba(91,50,180,0.15)] transition-all duration-500 hover:-translate-y-2 overflow-hidden"
            >
              <div className="absolute -right-4 -top-4 w-24 h-24 bg-[#5b32b4]/5 rounded-full blur-2xl group-hover:bg-[#fb397d]/10 transition-colors duration-500" />
              <div className="relative z-10 w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#5b32b4] to-[#7c4dff] text-white shadow-lg shadow-[#5b32b4]/30 mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                <span className="text-2xl font-black uppercase italic">
                  {category.name.charAt(0)}
                </span>
              </div>
              <div className="relative z-10 flex-grow">
                <h3 className="text-xl font-bold text-[#2b1d3a] group-hover:text-[#5b32b4] transition-colors mb-3">
                  {category.name}
                </h3>
                <p className="text-[15px] text-gray-500 leading-relaxed font-normal">
                  {category.description}
                </p>
              </div>
              <div className="relative z-10 mt-8 flex items-center justify-between">
                <span className="text-sm font-bold text-[#c3003a] uppercase tracking-wider group-hover:mr-2 transition-all">
                  Open Tools
                </span>
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-50 text-[#5b32b4] group-hover:bg-[#fb397d] group-hover:text-white transition-all duration-300">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="flex flex-col items-center space-y-4 pt-10">
          <Link
            href="/tools"
            prefetch={false}
            className="group relative px-10 py-4 overflow-hidden rounded-full bg-[#5b32b4] text-white font-bold shadow-xl transition-all hover:scale-105 active:scale-95"
          >
            <span className="relative z-10">Browse All Tool Categories</span>
            <div className="absolute inset-0 bg-[#fb397d] translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
          </Link>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
            No Account Required • 100% Free
          </p>
        </div>
      </section>
    </>
  );
}
